// src/config/standards/history.js

// C3 Framework (NCSS) – Dimension 2: History (D2.His.*)
// Domains used here:
// - Change, Continuity, and Context
// - Perspectives
// - Historical Sources and Evidence
// - Causation and Argumentation
//
// Grade bands (official) -> fanned out to each grade:
//   K–2  -> Kindergarten, 1st Grade, 2nd Grade
//   3–5  -> 3rd Grade, 4th Grade, 5th Grade
//   6–8  -> 6th Grade, 7th Grade, 8th Grade
//   9–12 -> High School
//
// Each standard keeps the C3 code (e.g., D2.His.1.K-2).
// We generate a unique `id` per grade: `History-<Grade>-<Code>`.

const BAND_FOR_GRADE = {
  "Kindergarten": "K-2",
  "1st Grade": "K-2",
  "2nd Grade": "K-2",
  "3rd Grade": "3-5",
  "4th Grade": "3-5",
  "5th Grade": "3-5",
  "6th Grade": "6-8",
  "7th Grade": "6-8",
  "8th Grade": "6-8",
  "High School": "9-12",
};

// Helper to build grade-specific standard objects from band entries
const buildForGrade = (grade, entries) =>
  entries.map(({ code, description }) => ({
    id: `History-${grade}-${code}`,
    code,
    description,
  }));

// ---- Domain content by band (official C3 wording) ----

const DOMAIN_CONTENT = {
  "Change, Continuity, and Context": {
    "K-2": [
      { code: "D2.His.1.K-2", description: "Create a chronological sequence of multiple events." },
      { code: "D2.His.2.K-2", description: "Compare life in the past to life today." },
      { code: "D2.His.3.K-2", description: "Generate questions about individuals and groups who have shaped a significant historical change." },
    ],
    "3-5": [
      { code: "D2.His.1.3-5", description: "Create and use a chronological sequence of related events to compare developments that happened at the same time." },
      { code: "D2.His.2.3-5", description: "Compare life in specific historical time periods to life today." },
      { code: "D2.His.3.3-5", description: "Generate questions about individuals and groups who have shaped significant historical changes and continuities." },
    ],
    "6-8": [
      { code: "D2.His.1.6-8", description: "Analyze connections among events and developments in broader historical contexts." },
      { code: "D2.His.2.6-8", description: "Classify series of historical events and developments as examples of change and/or continuity." },
      { code: "D2.His.3.6-8", description: "Use questions generated about individuals and groups to analyze why they, and the developments they shaped, are seen as historically significant." },
    ],
    "9-12": [
      { code: "D2.His.1.9-12", description: "Evaluate how historical events and developments were shaped by unique circumstances of time and place as well as broader historical contexts." },
      { code: "D2.His.2.9-12", description: "Analyze change and continuity in historical eras." },
      { code: "D2.His.3.9-12", description: "Use questions generated about individuals and groups to assess how the significance of their actions changes over time and is shaped by the historical context." },
    ],
  },

  "Perspectives": {
    "K-2": [
      { code: "D2.His.4.K-2", description: "Compare perspectives of people in the past to those of people in the present." },
      { code: "D2.His.6.K-2", description: "Compare different accounts of the same historical event." },
      // D2.His.5 begins at 3–5; D2.His.7 & D2.His.8 begin at 9–12
    ],
    "3-5": [
      { code: "D2.His.4.3-5", description: "Explain why individuals and groups during the same historical period differed in their perspectives." },
      { code: "D2.His.5.3-5", description: "Explain connections among historical contexts and people’s perspectives at the time." },
      { code: "D2.His.6.3-5", description: "Describe how people’s perspectives shaped the historical sources they created." },
    ],
    "6-8": [
      { code: "D2.His.4.6-8", description: "Analyze multiple factors that influenced the perspectives of people during different historical eras." },
      { code: "D2.His.5.6-8", description: "Explain how and why perspectives of people have changed over time." },
      { code: "D2.His.6.6-8", description: "Analyze how people’s perspectives influenced what information is available in the historical sources they created." },
    ],
    "9-12": [
      { code: "D2.His.4.9-12", description: "Analyze complex and interacting factors that influenced the perspectives of people during different historical eras." },
      { code: "D2.His.5.9-12", description: "Analyze how historical contexts shaped and continue to shape people’s perspectives." },
      { code: "D2.His.6.9-12", description: "Analyze the ways in which the perspectives of those writing history shaped the history that they produced." },
      { code: "D2.His.7.9-12", description: "Explain how the perspectives of people in the present shape interpretations of the past." },
      { code: "D2.His.8.9-12", description: "Analyze how current interpretations of the past are limited by the extent to which available historical sources represent perspectives of people at the time." },
    ],
  },

  "Historical Sources and Evidence": {
    "K-2": [
      { code: "D2.His.9.K-2", description: "Identify different kinds of historical sources." },
      { code: "D2.His.10.K-2", description: "Explain how historical sources can be used to study the past." },
      { code: "D2.His.11.K-2", description: "Identify the maker, date, and place of origin for a historical source from information within the source itself." },
      { code: "D2.His.12.K-2", description: "Generate questions about a particular historical source as it relates to a particular historical event or development." },
      // D2.His.13 begins at 3–5
    ],
    "3-5": [
      { code: "D2.His.9.3-5", description: "Summarize how different kinds of historical sources are used to explain events in the past." },
      { code: "D2.His.10.3-5", description: "Compare information provided by different historical sources about the past." },
      { code: "D2.His.11.3-5", description: "Infer the intended audience and purpose of a historical source from information within the source itself." },
      { code: "D2.His.12.3-5", description: "Generate questions about multiple historical sources and their relationships to particular historical events and developments." },
      { code: "D2.His.13.3-5", description: "Use information about a historical source, including the maker, date, place of origin, intended audience, and purpose to judge the extent to which the source is useful for studying a particular topic." },
    ],
    "6-8": [
      { code: "D2.His.9.6-8", description: "Classify the kinds of historical sources used in a secondary interpretation." },
      { code: "D2.His.10.6-8", description: "Detect possible limitations in the historical record based on evidence collected from different kinds of historical sources." },
      { code: "D2.His.11.6-8", description: "Use other historical sources to infer a plausible maker, date, place of origin, and intended audience for historical sources where this information is not easily identified." },
      { code: "D2.His.12.6-8", description: "Use questions generated about multiple historical sources to identify further areas of inquiry and additional sources." },
      { code: "D2.His.13.6-8", description: "Evaluate the relevancy and utility of a historical source based on information such as maker, date, place of origin, intended audience, and purpose." },
    ],
    "9-12": [
      { code: "D2.His.9.9-12", description: "Analyze the relationship between historical sources and the secondary interpretations made from them." },
      { code: "D2.His.10.9-12", description: "Detect possible limitations in various kinds of historical evidence and differing secondary interpretations." },
      { code: "D2.His.11.9-12", description: "Critique the usefulness of historical sources for a specific historical inquiry based on their maker, date, place of origin, intended audience, and purpose." },
      { code: "D2.His.12.9-12", description: "Use questions generated about multiple historical sources to pursue further inquiry and investigate additional sources." },
      { code: "D2.His.13.9-12", description: "Critique the appropriateness of the historical sources used in a secondary interpretation." },
    ],
  },

  "Causation and Argumentation": {
    "K-2": [
      { code: "D2.His.14.K-2", description: "Generate possible reasons for an event or development in the past." },
      { code: "D2.His.16.K-2", description: "Select which reasons might be more likely than others to explain a historical event or development." },
      // D2.His.15 begins at 6–8; D2.His.17 begins at 3–5
    ],
    "3-5": [
      { code: "D2.His.14.3-5", description: "Explain probable causes and effects of events and developments." },
      { code: "D2.His.16.3-5", description: "Use evidence to develop a claim about the past." },
      { code: "D2.His.17.3-5", description: "Summarize the central claim in a secondary work of history." },
    ],
    "6-8": [
      { code: "D2.His.14.6-8", description: "Explain multiple causes and effects of events and developments in the past." },
      { code: "D2.His.15.6-8", description: "Evaluate the relative influence of various causes of events and developments in the past." },
      { code: "D2.His.16.6-8", description: "Organize applicable evidence into a coherent argument about the past." },
      { code: "D2.His.17.6-8", description: "Compare the central arguments in secondary works of history on related topics in multiple media." },
    ],
    "9-12": [
      { code: "D2.His.14.9-12", description: "Analyze multiple and complex causes and effects of events in the past." },
      { code: "D2.His.15.9-12", description: "Distinguish between long-term causes and triggering events in developing a historical argument." },
      { code: "D2.His.16.9-12", description: "Integrate evidence from multiple relevant historical sources and interpretations into a reasoned argument about the past." },
      { code: "D2.His.17.9-12", description: "Critique the central arguments in secondary works of history on related topics in multiple media in terms of their historical accuracy." },
    ],
  },
};

// Build the full grade map for "History"
const buildHistoryGrades = () => {
  const grades = Object.keys(BAND_FOR_GRADE);

  const out = {};
  grades.forEach((grade) => {
    const band = BAND_FOR_GRADE[grade];
    out[grade] = {};

    Object.keys(DOMAIN_CONTENT).forEach((domain) => {
      const bandEntries = DOMAIN_CONTENT[domain][band] || [];
      out[grade][domain] = buildForGrade(grade, bandEntries);
    });
  });

  return out;
};

export const HISTORY_STANDARDS = {
  "History": buildHistoryGrades(),
};

export default HISTORY_STANDARDS;
